/**
 * The signer's phone proof for THIS browser (server: `signatureSignerSession.ts`). A correct SMS
 * code returns it, and every public signing call sends it back in the `X-Signer-Session` header:
 * the server opens a signer's document only to the browser that entered the code, never to whoever
 * holds the link.
 *
 * Kept in localStorage rather than sessionStorage because reopening the WhatsApp link opens a new
 * tab, and a signer should not need a second code within the session's two hours. A forwarded link
 * opened on another device has no session. Falls back to memory when storage is unavailable, so
 * the current page still works.
 */

const STORAGE_KEY = "webot-signer-session";
export const SIGNER_SESSION_HEADER = "X-Signer-Session";

type StoredSession = { token: string; expiresAt: number };

let inMemory: StoredSession | null = null;

const readStored = (): StoredSession | null => {
  let stored: StoredSession | null = inMemory;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) stored = JSON.parse(raw) as StoredSession;
  } catch {
    /* storage unavailable or unreadable: the in-memory copy stands */
  }
  if (!stored || typeof stored.token !== "string" || !stored.token) return null;
  if (!(typeof stored.expiresAt === "number" && stored.expiresAt > Date.now())) return null;
  return stored;
};

/** Store the session a verified code returned. Ignores a response without one (an older server). */
export const saveSignerSession = (token: unknown, expiresAt: unknown): void => {
  if (typeof token !== "string" || !token) return;
  const expiry = typeof expiresAt === "string" || typeof expiresAt === "number" ? new Date(expiresAt).getTime() : NaN;
  if (!Number.isFinite(expiry)) return;
  inMemory = { token, expiresAt: expiry };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(inMemory));
  } catch {
    /* storage unavailable: the in-memory copy serves this page */
  }
};

/** The header to send, or nothing when this browser holds no live session. */
export const signerSessionHeaders = (): Record<string, string> | undefined => {
  const stored = readStored();
  return stored ? { [SIGNER_SESSION_HEADER]: stored.token } : undefined;
};
